<template>
  <div class="login-root">
    <button class="login-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'"></i>
    </button>

    <div class="login-wrapper">
      <div class="login-card">
        <div class="text-center mb-4">
          <img
            :src="$brand.logo"
            :alt="brand.name"
            class="login-logo"
            @click="$router.push('/')"
          />
          <div class="login-brand mt-2">{{ brand.name }}</div>
          <p class="login-tagline">{{ brand.tagline }}</p>
        </div>

        <div v-if="$route.query.timeout" class="alert login-alert-info py-2 px-3 mb-3">
          <i class="mdi mdi-clock-alert-outline me-1"></i> You were signed out due to inactivity. Please sign in again.
        </div>

        <h5 class="text-center fw-bold mb-4 login-title">{{ mfaRequired ? 'Two-Factor Verification' : 'Welcome Back' }}</h5>

        <!-- ===== MFA STEP (shown after password is accepted) ===== -->
        <div v-if="mfaRequired">
          <div class="alert login-alert-info py-2 px-3 mb-3">
            <i class="mdi mdi-shield-key me-1"></i>
            Enter the 6-digit code from your authenticator app, or a 10-character backup code.
          </div>
          <div class="mb-3">
            <label class="auth-label">Verification code</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-numeric"></i></span>
              <input
                v-model="mfaCode"
                type="text"
                inputmode="numeric"
                class="form-control auth-input"
                placeholder="123456"
                autocomplete="one-time-code"
                autofocus
                @keyup.enter="submitMfa"
              />
            </div>
          </div>
          <div v-if="errorMsg" class="alert login-alert-danger py-2 px-3 mt-2">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>
          <button class="btn btn-primary w-100 mt-2" @click="submitMfa" :disabled="submitting || !mfaCode">
            <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Verifying…</span>
            <span v-else><i class="mdi mdi-check-circle me-1"></i> Verify and sign in</span>
          </button>
          <div class="text-center mt-3">
            <a href="javascript:void(0)" @click="cancelMfa" class="login-muted-link">
              <i class="mdi mdi-arrow-left me-1"></i> Use a different account
            </a>
          </div>
        </div>

        <!-- ===== STANDARD USERNAME/PASSWORD STEP ===== -->
        <div v-else>
        <div class="mb-3">
          <label class="auth-label">{{ t('login.username','Username') }}</label>
          <div class="input-group">
            <span class="input-group-text auth-input-icon"><i class="mdi mdi-account"></i></span>
            <input
              v-model="form.username"
              type="text"
              class="form-control auth-input"
              placeholder="e.g. dr.alex"
              autocomplete="username"
              @keyup.enter="login"
            />
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex justify-content-between">
            <label class="auth-label">{{ t('login.password','Password') }}</label>
            <a
              href="javascript:void(0)"
              @click="$router.push('/reset-password')"
              class="login-link login-link-sm"
            >
              {{ t('login.forgot','Forgot password?') }}
            </a>
          </div>
          <div class="input-group">
            <span class="input-group-text auth-input-icon"><i class="mdi mdi-lock"></i></span>
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="form.password"
              class="form-control auth-input"
              placeholder="Enter your password"
              autocomplete="current-password"
              @keyup.enter="login"
            />
            <button
              type="button"
              class="input-group-text password-toggle"
              @click="showPassword = !showPassword"
            >
              <i class="mdi" :class="showPassword ? 'mdi-eye-off' : 'mdi-eye'"></i>
            </button>
          </div>
        </div>

        <div class="mt-3" v-if="!isPatientPortalLogin">
          <label class="auth-label">{{ t('login.shift','Shift you are clocking in for') }}</label>
          <select v-model="form.shift_type" class="form-control auth-input">
            <option value="morning">Morning (07:00 – 14:00)</option>
            <option value="afternoon">Afternoon (14:00 – 21:00)</option>
            <option value="night">Night (21:00 – 07:00)</option>
            <option value="on_call">On-call</option>
            <option value="not_set">Not applicable (admin / desk)</option>
          </select>
        </div>

        <div v-if="errorMsg" class="alert login-alert-danger py-2 px-3 mt-2">
          <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
        </div>

        <button
          class="btn btn-primary w-100 mt-2"
          @click="login"
          :disabled="submitting || !form.username || !form.password"
        >
          <span v-if="submitting">
            <span class="spinner-border spinner-border-sm me-1"></span>
            Signing in...
          </span>
          <span v-else>
            <i class="mdi mdi-login me-1"></i> {{ t('login.sign_in','Sign In') }}
          </span>
        </button>

        <div v-if="brand.showOrgSignup" class="text-center mt-4 login-secondary-text">
          Don't have an account?
          <a href="javascript:void(0)" @click="$router.push('/signup')" class="login-link fw-semibold">
            Sign up your organisation
          </a>
        </div>

        <div class="text-center mt-3">
          <a href="javascript:void(0)" @click="$router.push('/')" class="login-muted-link">
            <i class="mdi mdi-arrow-left me-1"></i> Back to home
          </a>
        </div>
        </div><!-- /v-else (standard username/password step) -->
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #2d1111;
  --hms-alert-br:   #5c2020;
  --hms-alert-text: #ff6b6b;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #fff1f1;
  --hms-alert-br:   #f1b5b5;
  --hms-alert-text: #c43d3d;
}

html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.login-root {
  background: var(--hms-bg2) !important;
  min-height: 100vh;
  position: relative;
}

.login-theme-toggle {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--hms-bg3);
  border: 1px solid var(--hms-br);
  color: var(--hms-primary);
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .2s;
}
.login-theme-toggle:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px var(--hms-primary-lt);
}

.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  background: var(--hms-bg1) !important;
  border: 1px solid var(--hms-br);
  border-radius: 16px;
  padding: 40px 32px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
}

.login-logo {
  height: 52px;
  cursor: pointer;
}

.login-subtitle {
  color: var(--hms-s);
  font-size: 13px;
}

.login-brand {
  color: var(--hms-primary);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
}

.login-tagline {
  color: var(--hms-s);
  font-size: 12px;
  font-style: italic;
  margin-top: 2px;
}

.login-alert-info {
  background: var(--hms-primary-lt);
  border: 1px solid var(--hms-primary);
  color: var(--hms-primary);
  font-size: 13px;
  border-radius: 8px;
}

.login-title {
  color: var(--hms-h);
}

.auth-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--hms-m) !important;
  margin-bottom: 6px;
  display: block;
}

.auth-input,
.auth-input-icon,
.password-toggle {
  background: var(--hms-bg4) !important;
  border-color: var(--hms-input-br) !important;
  color: var(--hms-h) !important;
}

.auth-input-icon {
  color: var(--hms-s) !important;
}

.auth-input:focus,
.password-toggle:focus {
  border-color: var(--hms-primary) !important;
  box-shadow: 0 0 0 2px var(--hms-primary-lt) !important;
  outline: none;
}

.auth-input {
  border-radius: 8px;
}

.auth-input::placeholder {
  color: var(--hms-placeholder) !important;
}

.password-toggle {
  cursor: pointer;
}

.login-link {
  color: var(--hms-primary);
  text-decoration: none;
}
.login-link:hover { text-decoration: underline; }

.login-link-sm {
  font-size: 12px;
}

.login-alert-danger {
  background: var(--hms-alert-bg);
  border: 1px solid var(--hms-alert-br);
  color: var(--hms-alert-text);
  font-size: 13px;
}

.login-secondary-text {
  color: var(--hms-s);
  font-size: 13px;
}

.login-muted-link {
  color: var(--hms-s);
  font-size: 12px;
  text-decoration: none;
}
.login-muted-link:hover { color: var(--hms-primary); }

@media (max-width: 480px) {
  .login-card {
    padding: 28px 20px;
    border-radius: 12px;
  }
}
</style>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";
import brand from "@/brand";

export default {
  name: "Login",
  data() {
    return {
      brand,
      form: { username: "", password: "", shift_type: "morning" },
      showPassword: false,
      submitting: false,
      errorMsg: "",
      successMsg: "",
      mfaRequired: false,
      mfaToken: "",
      mfaCode: "",
      isPatientPortalLogin: false,
      i18n: useI18n(),
      darkMode: (localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light") !== "light",
      themeObserver: null
    };
  },
  mounted() {
    this.applyTheme();
    this.watchTheme();

    var u = sessionStorage.getItem("userId");
    if (u) this.$router.push("/dashboard");
  },
  beforeUnmount() {
    if (this.themeObserver) this.themeObserver.disconnect();
    window.removeEventListener("storage", this.applyTheme);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
      this.darkMode = t !== "light";
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      var theme = this.darkMode ? "dark" : "light";
      localStorage.setItem("hms_theme", theme);
      document.documentElement.setAttribute("data-theme", theme);
    },
    watchTheme() {
      var self = this;
      this.themeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
            self.darkMode = document.documentElement.getAttribute("data-theme") !== "light";
          }
        });
      });
      this.themeObserver.observe(document.documentElement, { attributes: true });
      window.addEventListener("storage", this.applyTheme);
    },
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    captureLoginLocation() {
      var self = this;
      var post = function(lat, lng, acc) {
        var p = { function: "trackLoginLocation", shift_type: self.form.shift_type || "not_set" };
        if (lat != null) p.lat = lat;
        if (lng != null) p.lng = lng;
        if (acc != null) p.accuracy_m = acc;
        executeJson(p, hms).catch(function(){});
      };
      if (!navigator.geolocation) { post(); return; }
      try {
        navigator.geolocation.getCurrentPosition(
          function(pos) { post(pos.coords.latitude, pos.coords.longitude, Math.round(pos.coords.accuracy || 0)); },
          function()    { post(); },
          { enableHighAccuracy: false, timeout: 4000 }
        );
      } catch(e) { post(); }
    },

    async login() {
      if (!this.form.username || !this.form.password) return;
      this.submitting = true;
      this.errorMsg = "";
      try {
        var r = await executeJson(
          {
            function: "loginUser",
            username: this.form.username.trim(),
            password: this.form.password,
            deviceType: "web"
          },
          hms
        );
        if (r.data.success) {
          // MFA gate — if the backend says password OK but MFA required, show the code step
          if (r.data.data && r.data.data.mfa_required) {
            this.mfaRequired = true;
            this.mfaToken    = r.data.data.mfa_token || "";
            this.errorMsg    = "";
            this.submitting  = false;
            return;
          }
          var user  = r.data.data.user  || {};
          var token = r.data.data.token || "";

          sessionStorage.setItem("userId",     user.user_id || "");
          sessionStorage.setItem("isLoggedIn", "true");
          sessionStorage.setItem("userName",   user.username || "");
          sessionStorage.setItem("userRole",   user.role_code || "");
          sessionStorage.setItem("allowedModules", user.allowed_modules || "");
          sessionStorage.setItem("hospitalId", user.hospital_id || "");
          sessionStorage.setItem("branchId",   user.branch_id || "");
          sessionStorage.setItem("staffId",    user.staff_id || "");
          if (user.first_name || user.last_name) {
            sessionStorage.setItem("displayName", ((user.first_name || "") + " " + (user.last_name || "")).trim());
          }
          if (user.profile_photo) sessionStorage.setItem("userPhoto", user.profile_photo);

          localStorage.setItem("authToken", token);

          // Persist full user profile for the multi-tenant guards (PlatformConsole, OrgAdminDashboard)
          localStorage.setItem("userProfile", JSON.stringify({
            user_id:         user.user_id,
            username:        user.username,
            user_scope:      user.user_scope || "hospital",
            organization_id: user.organization_id || null,
            hospital_id:     user.hospital_id,
            branch_id:       user.branch_id,
            role_code:       user.role_code,
            role_name:       user.role_name,
            first_name:      user.first_name,
            last_name:       user.last_name
          }));

          // Capture GPS + shift_type for compliance audit (non-blocking).
          this.captureLoginLocation();

          if (user.must_change_password == 1 || user.must_change_password === "1") {
            this.$router.push("/change-password");
          } else if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          } else {
            // Multi-tenant routing by user_scope
            var scope = user.user_scope || "hospital";
            if (scope === "platform") {
              this.$router.push("/platform");
            } else if (scope === "organization") {
              this.$router.push("/org");
            } else {
              this.$router.push("/dashboard");
            }
          }
        } else {
          this.errorMsg = r.data.message || "Invalid credentials. Please try again.";
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    },

    async submitMfa() {
      if (!this.mfaCode || !this.mfaToken) return;
      this.submitting = true;
      this.errorMsg = "";
      try {
        var r = await executeJson(
          { function: "completeMfaLogin", mfa_token: this.mfaToken, code: this.mfaCode.trim() },
          hms
        );
        if (r.data.success) {
          var user  = r.data.data.user  || {};
          var token = r.data.data.token || this.mfaToken;
          sessionStorage.setItem("userId",     user.user_id || "");
          sessionStorage.setItem("isLoggedIn", "true");
          sessionStorage.setItem("userName",   user.username || "");
          sessionStorage.setItem("userRole",   user.role_code || "");
          sessionStorage.setItem("allowedModules", user.allowed_modules || "");
          sessionStorage.setItem("hospitalId", user.hospital_id || "");
          sessionStorage.setItem("branchId",   user.branch_id || "");
          sessionStorage.setItem("staffId",    user.staff_id || "");
          if (user.first_name || user.last_name) {
            sessionStorage.setItem("displayName", ((user.first_name || "") + " " + (user.last_name || "")).trim());
          }
          localStorage.setItem("authToken", token);
          localStorage.setItem("userProfile", JSON.stringify({
            user_id:         user.user_id,
            username:        user.username,
            user_scope:      user.user_scope || "hospital",
            organization_id: user.organization_id || null,
            hospital_id:     user.hospital_id,
            branch_id:       user.branch_id,
            role_code:       user.role_code,
            role_name:       user.role_name,
            first_name:      user.first_name,
            last_name:       user.last_name
          }));
          this.captureLoginLocation();
          this.mfaRequired = false;
          this.mfaToken = "";
          this.mfaCode = "";
          var scope = user.user_scope || "hospital";
          if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          } else if (scope === "platform") {
            this.$router.push("/platform");
          } else if (scope === "organization") {
            this.$router.push("/org");
          } else {
            this.$router.push("/dashboard");
          }
        } else {
          this.errorMsg = r.data.message || "Invalid code.";
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    },

    cancelMfa() {
      this.mfaRequired = false;
      this.mfaToken = "";
      this.mfaCode = "";
      this.errorMsg = "";
      this.form.password = "";
    }
  }
};
</script>
